import { Ticket, CalendarClock } from "lucide-react";
import { fmtDate } from "../../data/plannerDates";

// Why each bookable stop is worth sorting out before the trip. Everything
// here has a `gyg` link on its spot (partner HIOKDT8) — this panel just
// gathers them in one place so nobody has to hunt through the day cards.
export const BOOK_AHEAD = {
  tivoli: { kind: "Timed entry", tip: "Entry + ride pass sells out on weekends and all through the Christmas season." },
  rosenborg: { kind: "Timed slot", tip: "Small rooms, timed slots — summer mornings go first." },
  christiansborg: { kind: "Ticket", tip: "One ticket covers the royal rooms, ruins and stables — skip the desk queue." },
  nationalmuseet: { kind: "Ticket", tip: "Buy ahead to walk straight in — the queue builds by late morning." },
  dac: { kind: "Ticket", tip: "Includes the rooftop and exhibitions — no queue with a pre-bought ticket." },
  canaltour: { kind: "Departure", tip: "Pick a departure time — the Nyhavn boats fill up from midday in summer." },
  experimentarium: { kind: "Ticket", tip: "Rainy days and school holidays are packed — book the morning." },
  christiania: { kind: "Guided tour · optional", tip: "Tickets are for a local-led walk — free to wander otherwise." },
  opera: { kind: "Guided tour · optional", tip: "The inside is guided-tour only; the park and foyer are free." },
  copenhill: { kind: "Time slot", tip: "Ski and hike slots are limited — the free rooftop stairs need no booking." },
  cc: { kind: "Ticket", tip: "Pre-book to skip the desk — combine with a Reffen lunch next door." },
  zoo: { kind: "Ticket", tip: "Skip the gate queue — weekends and holidays are busiest." },
  carlsberg: { kind: "Ticket", tip: "Includes the tasting — afternoon slots go first." },
};

export const bookableStops = (plan) =>
  plan.flatMap((d) => d.items.filter((s) => s.gyg && BOOK_AHEAD[s.id]).map((s) => ({ s, day: d })));

// One list of every stop in the plan that needs (or benefits from) a
// pre-booked ticket, grouped in day order with the affiliate booking link.
export const BookAhead = ({ plan }) => {
  const rows = bookableStops(plan);
  if (!rows.length) return null;
  return (
    <section data-testid="planner-book-ahead" className="mt-6 bg-white border border-black shadow-brut-sm rounded-2xl p-5 md:p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
        <h3 className="font-display font-900 text-xl inline-flex items-center gap-2">
          <CalendarClock size={18} className="text-[#DF3226]" /> Book ahead
        </h3>
        <p className="font-body text-xs text-black/50">
          {rows.length} stop{rows.length > 1 ? "s" : ""} on your plan worth booking ahead · <span className="tracking-[0.15em] font-800">AD · GETYOURGUIDE</span>
        </p>
      </div>
      <ul className="divide-y divide-zinc-100">
        {rows.map(({ s, day }) => {
          const b = BOOK_AHEAD[s.id];
          return (
            <li key={`${day.day}-${s.id}`} data-testid={`planner-book-ahead-${s.id}`} className="py-3 flex items-start gap-3">
              <span className="shrink-0 w-[4.6rem] font-display font-900 text-xs text-black/70 leading-tight pt-0.5">
                Day {day.day}
                {day.date && <span className="block font-body font-700 text-[10px] text-black/45">{fmtDate(day.date)}</span>}
                {day.timeline?.at && (
                  <span className="block font-body font-700 text-[10px] text-black/45">
                    ~{day.timeline.at[day.items.findIndex((x) => x.id === s.id)]}
                  </span>
                )}
              </span>
              <span className="flex-1 min-w-0">
                <span className="font-body font-800 text-sm block">{s.name}</span>
                <span className="font-body text-xs text-black/60 block leading-snug">
                  <span className="font-700 text-[#DF3226]">{b.kind}</span> — {b.tip}
                </span>
              </span>
              <a
                href={s.gyg}
                target="_blank"
                rel="noopener noreferrer sponsored"
                data-testid={`planner-book-ahead-link-${s.id}`}
                className="shrink-0 inline-flex items-center gap-1 font-display font-800 text-xs bg-black text-white px-3 py-1.5 rounded-full hover:bg-[#DF3226] transition-colors"
              >
                <Ticket size={12} /> Tickets
              </a>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
